import React, { ChangeEvent } from 'react';
import Button from 'src/components/Button';
import RadioButtonGroup from 'src/components/RadioButtonGroup/RadioButtonGroup';
import FontAwesomeIcon from 'src/components/Icon';
import BulkWalkthroughsSuiteTable from 'src/components/BulkOperations/Walkthroughs/BulkWalkthroughsSuiteTable/BulkWalkthroughsSuiteTable';
import WalkthroughActivationSection from 'src/components/WalkthroughActivationSection/WalkthroughActivationSection';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import {
  useBulkAddWalkthroughs,
  UseBulkAddWalkthroughsReturn,
} from 'src/components/BulkOperations/Walkthroughs/BulkAddWalkthroughs/Custom-Hooks/useBulkAddWalkthroughs';
import {
  IUseBookingLinkSelection,
  useBookingLinkSelection,
} from 'src/pages/Calendar/CalcomIntegration/CustomHooks/useBookingLinkSelection/useBookingLinkSelection';
import { ICalcomConnectedCalendar } from 'src/pages/Calendar/CalcomIntegration/types';
import CalcomSchedulingConfirmationNote from 'src/pages/Calendar/CalcomIntegration/components/CalcomSchedulingConfirmationNote/CalcomSchedulingConfirmationNote';
import './BulkAddWalkthroughs.scss';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { variables } from 'src/Tenants';

interface ActivationActions {
  setActivate: (value: boolean) => void;
  setSendEmail: (value: boolean) => void;
}

interface BulkAddWalkthroughsProps {
  userId: string;
  selectedSuiteIds: string[];
  onSave?: (params: any) => void;
  suites: UnitBulkOperationDto[];
  isSubmitting: boolean;
  propertyId: string;
  isCalcomConnected: boolean;
  connectedCalendars: ICalcomConnectedCalendar[];
}

const BulkAddWalkthroughs = ({
  userId,
  selectedSuiteIds: initialSelectedSuiteIds,
  onSave,
  suites,
  isSubmitting,
  propertyId,
  isCalcomConnected,
  connectedCalendars,
}: BulkAddWalkthroughsProps) => {
  const { selectedBookingLinkId, selectBookingLink }: IUseBookingLinkSelection =
    useBookingLinkSelection();

  const {
    walkthroughName,
    setWalkthroughName,
    description,
    setDescription,
    walkthroughType,
    setWalkthroughType,
    permissionType,
    setPermissionType,
    checklistItems,
    handleAddChecklistItem,
    handleRemoveChecklistItem,
    handleChecklistItemChange,
    isChecklistItemEmpty,
    selectedSuiteIds,
    handleSuiteSelectAll,
    handleSuiteSelectIndividual,
    handleDeselectAllSuites,
    activateWalkthroughs,
    setActivateWalkthroughs,
    sendSchedulingEmail,
    handleSendSchedulingEmailChange,
    isFormValid,
    saveBulkWalkthroughs,
    showConfirmation,
    confirmSave,
    setShowConfirmation,
  }: UseBulkAddWalkthroughsReturn = useBulkAddWalkthroughs({
    userId,
    initialSelectedSuiteIds,
    onSave,
    suites,
    bookingLinkId: selectedBookingLinkId,
  });

  const activationActions: ActivationActions = {
    setActivate: setActivateWalkthroughs,
    setSendEmail: handleSendSchedulingEmailChange,
  };

  // Mirror the activate flow: a Cal.com scheduling email needs a booking link selected.
  const isMissingBookingLinkForCalComEmail: boolean =
    sendSchedulingEmail && isCalcomConnected && !selectedBookingLinkId;

  const isCalcomSchedulingActive: boolean =
    sendSchedulingEmail && isCalcomConnected && Boolean(selectedBookingLinkId);

  const walkthroughTypeOptions: any = [
    { value: 'Simplified', label: 'Simplified' },
    { value: 'Enhanced', label: 'Enhanced' },
  ];

  const permissionTypeOptions: any = [
    { value: 'Internal', label: 'Internal' },
    { value: 'Homeowner or Occupant', label: 'Homeowner or Occupant' },
  ];

  const suiteName: string = variables.suite;
  const suiteNames: string = variables.suites;

  return (
    <div className="walkthrough-form-container">
      {showConfirmation && (
        <ConfirmationModal
          title="Confirm Bulk Creation"
          message={
            <>
              You are about to create{' '}
              <strong>{selectedSuiteIds.length} walkthroughs</strong>.
            </>
          }
          warningMessage={
            <>
              <strong>Please double-check your settings.</strong> Currently,
              walkthroughs cannot be edited or deleted in bulk once they are
              created.
            </>
          }
          confirmLabel="Confirm & Create"
          onConfirm={confirmSave}
          onCancel={() => setShowConfirmation(false)}
          isSubmitting={isSubmitting}
          variant="warning"
          confirmationWord="ADD"
        >
          {isCalcomSchedulingActive && <CalcomSchedulingConfirmationNote />}
        </ConfirmationModal>
      )}
      <div className="walkthrough-form-content">
        <div className="walkthrough-form-row">
          <div className="walkthrough-form-column">
            <div className="walkthrough-input-group">
              <label className="walkthrough-form-label-bulk-add-walkthroughs">
                Name<span className="required-field-indicator">*</span>
              </label>
              <input
                type="text"
                className="walkthrough-text-input"
                value={walkthroughName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setWalkthroughName(e.target.value)
                }
                placeholder="Walkthrough Name"
                required
              />
            </div>
            {/* Walkthrough Type and Permissions Row. */}
            <div className="walkthrough-radio-groups-container">
              <RadioButtonGroup
                name="permissionType"
                options={permissionTypeOptions}
                value={permissionType}
                onChange={setPermissionType}
                label="Permissions:"
                required={true}
                className="walkthrough-radio-group"
              />
              <RadioButtonGroup
                name="walkthroughType"
                options={walkthroughTypeOptions}
                value={walkthroughType}
                onChange={setWalkthroughType}
                label="Walkthrough Type:"
                required={true}
                className="walkthrough-radio-group"
              />
            </div>
            {/* Homeowner-specific options */}
            {permissionType === 'Homeowner or Occupant' && (
              <>
                <label className="walkthrough-form-label-bulk-add-walkthroughs">
                  Activation Settings
                </label>
                <WalkthroughActivationSection
                  activateWalkthroughs={activateWalkthroughs}
                  sendSchedulingEmail={sendSchedulingEmail}
                  actions={activationActions}
                  propertyId={propertyId}
                  isCalcomConnected={isCalcomConnected}
                  connectedCalendars={connectedCalendars}
                  selectedBookingLinkId={selectedBookingLinkId}
                  onSelectBookingLink={selectBookingLink}
                />
              </>
            )}
            {/* Enhanced Walkthrough Checklist Items. */}
            {walkthroughType === 'Enhanced' && (
              <div className="walkthrough-input-group">
                <label className="walkthrough-form-label-bulk-add-walkthroughs">
                  Checklist Items
                  <span className="required-field-indicator">*</span>
                </label>
                <div className="walkthrough-checklist-section">
                  {checklistItems.map((item: string, index: number) => (
                    <div key={index} className="walkthrough-checklist-item-row">
                      <div className="walkthrough-checklist-item-container">
                        <input
                          type="text"
                          className={`walkthrough-text-input ${
                            isChecklistItemEmpty(index)
                              ? 'walkthrough-input-error'
                              : ''
                          }`}
                          value={item}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleChecklistItemChange(index, e.target.value)
                          }
                          placeholder={`Checklist item ${index + 1}`}
                        />
                        {isChecklistItemEmpty(index) && (
                          <div className="walkthrough-error-message">
                            Checklist item cannot be empty
                          </div>
                        )}
                      </div>
                      {checklistItems.length > 1 && (
                        <button
                          type="button"
                          className="checklist-item-remove-button"
                          onClick={() => handleRemoveChecklistItem(index)}
                        >
                          <FontAwesomeIcon glyph="trash" weight="light" />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button
                    color="teal"
                    size="small"
                    label="+ Add Checklist Item"
                    onClick={handleAddChecklistItem}
                  />
                </div>
              </div>
            )}
            {/* Description. */}
            <div className="walkthrough-input-group">
              <label className="walkthrough-form-label-bulk-add-walkthroughs">
                Description
              </label>
              <textarea
                className="walkthrough-description-textarea"
                value={description}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setDescription(e.target.value)
                }
                placeholder="Description"
                rows={3}
              />
            </div>
            {/* Scrollable Suite Selection Table Section. */}
            <div className="walkthrough-input-group">
              <label className="walkthrough-form-label-bulk-add-walkthroughs">
                Choose {suiteName} for Walkthrough ({selectedSuiteIds.length} of{' '}
                {suites.length} selected)
              </label>
              <div className="walkthrough-suite-table-section">
                <div className="walkthrough-suite-table-wrapper">
                  <BulkWalkthroughsSuiteTable
                    suites={suites}
                    selectedSuiteIds={selectedSuiteIds}
                    onSuiteSelectAll={handleSuiteSelectAll}
                    handleSuiteSelectIndividual={handleSuiteSelectIndividual}
                    onDeselectAll={handleDeselectAllSuites}
                    sendSchedulingEmail={sendSchedulingEmail}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Actions - Now outside the scrollable area. */}
      <div className="walkthrough-form-actions">
        <LoadingButton
          actionText="Save Walkthroughs"
          selectedCount={selectedSuiteIds.length}
          countLabel={selectedSuiteIds.length === 1 ? suiteName : suiteNames}
          isSubmitting={isSubmitting}
          disabled={!isFormValid() || isMissingBookingLinkForCalComEmail}
          onClick={saveBulkWalkthroughs}
        />
      </div>
    </div>
  );
};

export default BulkAddWalkthroughs;
